/**
 * AdminUsers — /admin/users
 * Paginated user table. Click user → modal with last 10 bets + ledger + restrict button.
 */
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  adminListUsers,
  adminGetUser,
  adminRestrictUser,
  type AdminUserSummary,
  type AdminUserDetail,
} from '../../services/api';
import { formatZAR } from '../../utils/currency';

interface AdminCtx {
  adminEmail: string | null;
}

interface UserDetailModalProps {
  userId: string;
  email: string;
  onClose: () => void;
  onRefresh: () => void;
}

function UserDetailModal({ userId, email, onClose, onRefresh }: UserDetailModalProps) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    adminGetUser(email, userId)
      .then((r) => setDetail(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [email, userId]);

  const handleRestrict = async (action: 'restrict' | 'unrestrict') => {
    setActionLoading(true);
    try {
      await adminRestrictUser(email, userId, action);
      onRefresh();
      onClose();
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-8 overflow-y-auto">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="font-bold text-lg text-gray-900">User Detail</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>
        {loading && (
          <div className="px-6 py-10 text-center text-sm text-gray-400">Loading...</div>
        )}
        {detail && (
          <div className="px-6 py-5 space-y-5">
            {/* Profile */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-400 text-xs">Email</p>
                <p className="font-medium text-gray-800">{detail.email}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">KYC Tier</p>
                <p className="font-medium text-gray-800">{detail.kyc_tier}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Balance</p>
                <p className="font-medium text-gray-800">{formatZAR(detail.balance_cents)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Status</p>
                <p className="font-medium">
                  {detail.is_self_excluded ? (
                    <span className="text-red-600">Self-excluded</span>
                  ) : detail.is_active ? (
                    <span className="text-green-600">Active</span>
                  ) : (
                    <span className="text-red-500">Restricted</span>
                  )}
                </p>
              </div>
              {detail.is_sharp_bettor && (
                <div className="col-span-2">
                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                    Sharp bettor
                  </span>
                </div>
              )}
            </div>

            {/* Last 10 bets */}
            <div>
              <h3 className="mb-2 font-semibold text-sm text-gray-700">Last 10 Bets</h3>
              {(detail.last_bets as Record<string, unknown>[]).length === 0 ? (
                <p className="text-xs text-gray-400">No bets yet.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-100 text-xs">
                  <thead>
                    <tr className="text-left text-gray-400">
                      <th className="pb-1">Type</th>
                      <th className="pb-1">Status</th>
                      <th className="pb-1 text-right">Stake</th>
                      <th className="pb-1 text-right">Payout</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(detail.last_bets as Record<string, unknown>[]).map((b) => (
                      <tr key={String(b.id)}>
                        <td className="py-1 capitalize">{String(b.bet_type)}</td>
                        <td className="py-1 capitalize">{String(b.status)}</td>
                        <td className="py-1 text-right font-mono">
                          {formatZAR(Number(b.total_stake_cents))}
                        </td>
                        <td className="py-1 text-right font-mono">
                          {b.actual_payout_cents != null
                            ? formatZAR(Number(b.actual_payout_cents))
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Last 10 ledger */}
            <div>
              <h3 className="mb-2 font-semibold text-sm text-gray-700">Last 10 Ledger Entries</h3>
              {(detail.last_ledger as Record<string, unknown>[]).length === 0 ? (
                <p className="text-xs text-gray-400">No ledger entries.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-100 text-xs">
                  <thead>
                    <tr className="text-left text-gray-400">
                      <th className="pb-1">Type</th>
                      <th className="pb-1 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(detail.last_ledger as Record<string, unknown>[]).map((l) => (
                      <tr key={String(l.id)}>
                        <td className="py-1">{String(l.txn_type)}</td>
                        <td
                          className={`py-1 text-right font-mono ${
                            Number(l.amount_cents) >= 0 ? 'text-green-600' : 'text-red-500'
                          }`}
                        >
                          {formatZAR(Number(l.amount_cents))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Restrict/Unrestrict */}
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
              {detail.is_active ? (
                <button
                  disabled={actionLoading}
                  onClick={() => handleRestrict('restrict')}
                  className="rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200 disabled:opacity-60"
                >
                  Restrict account
                </button>
              ) : (
                <button
                  disabled={actionLoading}
                  onClick={() => handleRestrict('unrestrict')}
                  className="rounded-lg bg-green-100 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-200 disabled:opacity-60"
                >
                  Unrestrict account
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  const load = async (p: number) => {
    if (!adminEmail) return;
    setLoading(true);
    try {
      const res = await adminListUsers(adminEmail, p);
      setUsers(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminEmail) load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail, page]);

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">
        Users <span className="text-base font-normal text-gray-400">({total})</span>
      </h1>

      {selectedUser && adminEmail && (
        <UserDetailModal
          userId={selectedUser}
          email={adminEmail}
          onClose={() => setSelectedUser(null)}
          onRefresh={() => load(page)}
        />
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">KYC</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Flags</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                  No users yet.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u.user_id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{u.email}</td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-700">
                    T{u.kyc_tier}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-sm font-mono text-gray-700">
                  {formatZAR(u.balance_cents)}
                </td>
                <td className="px-4 py-3 text-center text-xs">
                  {u.is_sharp_bettor && (
                    <span className="mr-1 rounded-full bg-orange-100 px-1.5 py-0.5 text-orange-700">
                      Sharp
                    </span>
                  )}
                  {u.is_self_excluded && (
                    <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-red-700">
                      Excluded
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSelectedUser(u.user_id)}
                    className="text-xs font-medium text-violet-600 hover:text-violet-800"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-sm text-gray-500">
            {page} / {pages}
          </span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
